import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailSettingsManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  
  const [settings, setSettings] = useState({
    enabled: false,
    sender_email: '',
    sender_name: 'RijekaBooking',
    owner_email: '',
    subject_hr: 'Potvrda rezervacije - {{property_name}}',
    subject_en: 'Booking Confirmation - {{property_name}}',
    subject_it: 'Conferma prenotazione - {{property_name}}',
    subject_de: 'Buchungsbestätigung - {{property_name}}',
    intro_text_hr: 'Hvala Vam na rezervaciji! Vaša rezervacija je potvrđena. U nastavku pronađite sve detalje Vašeg boravka.',
    intro_text_en: 'Thank you for your booking! Your reservation is confirmed. Below you will find all the details of your stay.',
    intro_text_it: 'Grazie per la prenotazione! La tua prenotazione è confermata. Di seguito troverai tutti i dettagli del tuo soggiorno.',
    intro_text_de: 'Vielen Dank für Ihre Buchung! Ihre Reservierung ist bestätigt. Nachfolgend finden Sie alle Details zu Ihrem Aufenthalt.',
    footer_text_hr: 'Ako imate pitanja, slobodno nas kontaktirajte. Radujemo se Vašem dolasku!',
    footer_text_en: 'If you have any questions, feel free to contact us. We look forward to your arrival!',
    footer_text_it: 'Se hai domande, non esitare a contattarci. Non vediamo l\'ora di accoglierti!',
    footer_text_de: 'Bei Fragen können Sie uns gerne kontaktieren. Wir freuen uns auf Ihre Ankunft!',
    button_text_hr: 'Pogledaj objekt',
    button_text_en: 'View Property',
    button_text_it: 'Vedi proprietà',
    button_text_de: 'Unterkunft ansehen',
    button_url: ''
  });

  useEffect(() => {
    fetchSettings();
    checkApiKey();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/email-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching email settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkApiKey = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/resend-api-key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeyConfigured(response.data.configured);
    } catch (error) {
      console.error('Error checking API key:', error);
    }
  };

  const saveApiKey = async () => {
    if (!apiKey.trim()) {
      toast.error('Unesite API ključ');
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/resend-api-key`, { api_key: apiKey }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Resend API ključ spremljen');
      setApiKeyConfigured(true);
      setShowApiKeyInput(false);
      setApiKey('');
    } catch (error) {
      toast.error('Greška pri spremanju API ključa');
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/email-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Postavke emaila spremljene');
    } catch (error) {
      toast.error('Greška pri spremanju postavki');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmailAddress.trim()) {
      toast.error('Unesite email adresu za test');
      return;
    }
    try {
      setSendingTest(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/test-email`, { recipient_email: testEmailAddress }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Test email poslan na ${testEmailAddress}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greška pri slanju test emaila');
    } finally {
      setSendingTest(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* API Key Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            Resend API Konfiguracija
          </CardTitle>
          <CardDescription>
            Povežite Resend servis za slanje email obavijesti
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className={`w-3 h-3 rounded-full ${apiKeyConfigured ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-sm">
                {apiKeyConfigured ? 'API ključ konfiguriran' : 'API ključ nije konfiguriran'}
              </span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowApiKeyInput(!showApiKeyInput)}
              >
                {apiKeyConfigured ? 'Promijeni ključ' : 'Dodaj ključ'}
              </Button>
            </div>
            
            {showApiKeyInput && (
              <div className="flex gap-2 mt-4">
                <Input
                  type="password"
                  placeholder="re_xxxxxxxx..."
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="font-mono"
                />
                <Button onClick={saveApiKey} disabled={saving}>
                  {saving ? 'Spremanje...' : 'Spremi'}
                </Button>
              </div>
            )}
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
              <p className="font-medium text-blue-800 mb-2">Kako dobiti Resend API ključ:</p>
              <ol className="list-decimal list-inside space-y-1 text-blue-700">
                <li>Posjetite <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline">resend.com</a> i kreirajte račun</li>
                <li>Idite na Dashboard → API Keys</li>
                <li>Kliknite &quot;Create API Key&quot;</li>
                <li>Kopirajte ključ (počinje s <code className="bg-blue-100 px-1 rounded">re_</code>)</li>
                <li>Verificirajte vašu domenu za slanje s vlastite adrese</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Enable/Disable */}
      <Card>
        <CardHeader>
          <CardTitle>Email Obavijesti</CardTitle>
          <CardDescription>
            Automatsko slanje potvrde rezervacije gostima
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Uključi email obavijesti</p>
              <p className="text-sm text-gray-500">Gosti će primiti email nakon uspješne rezervacije</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => handleChange('enabled', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {settings.enabled && (
        <>
          {/* Sender Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Postavke Pošiljatelja</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Email pošiljatelja</Label>
                  <Input
                    type="email"
                    placeholder="rezervacije@rijekabooking.com"
                    value={settings.sender_email}
                    onChange={(e) => handleChange('sender_email', e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">Domena mora biti verificirana u Resend-u</p>
                </div>
                <div>
                  <Label>Ime pošiljatelja</Label>
                  <Input
                    placeholder="RijekaBooking"
                    value={settings.sender_name}
                    onChange={(e) => handleChange('sender_name', e.target.value)}
                  />
                </div>
              </div>
              
              <div>
                <Label>Email vlasnika (kopija rezervacija)</Label>
                <Input
                  type="email"
                  placeholder="vlasnik@rijekabooking.com"
                  value={settings.owner_email}
                  onChange={(e) => handleChange('owner_email', e.target.value)}
                  data-testid="owner-email-input"
                />
                <p className="text-xs text-gray-500 mt-1">Na ovu adresu stiže kopija svake nove rezervacije. Ostavite prazno ako ne želite kopije.</p>
              </div>
              
              <div>
                <Label>URL gumba u emailu</Label>
                <Input
                  type="url"
                  placeholder="https://booking.rijekabooking.com"
                  value={settings.button_url}
                  onChange={(e) => handleChange('button_url', e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">Link na koji će voditi gumb &quot;Pogledaj objekt&quot; u emailu</p>
              </div>
            </CardContent>
          </Card>

          {/* Language Tabs for Content */}
          <Card>
            <CardHeader>
              <CardTitle>Sadržaj Emaila</CardTitle>
              <CardDescription>Prilagodite tekst za svaki jezik</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="hr">
                <TabsList className="mb-4">
                  <TabsTrigger value="hr">🇭🇷 Hrvatski</TabsTrigger>
                  <TabsTrigger value="en">🇬🇧 English</TabsTrigger>
                  <TabsTrigger value="it">🇮🇹 Italiano</TabsTrigger>
                  <TabsTrigger value="de">🇩🇪 Deutsch</TabsTrigger>
                </TabsList>

                {['hr', 'en', 'it', 'de'].map((lang) => (
                  <TabsContent key={lang} value={lang} className="space-y-4">
                    <div>
                      <Label>Predmet emaila</Label>
                      <Input
                        value={settings[`subject_${lang}`]}
                        onChange={(e) => handleChange(`subject_${lang}`, e.target.value)}
                        placeholder="Potvrda rezervacije - {{property_name}}"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Koristite <code className="bg-gray-100 px-1 rounded">{'{{property_name}}'}</code> za ime objekta
                      </p>
                    </div>

                    <div>
                      <Label>Uvodni tekst</Label>
                      <Textarea
                        rows={3}
                        value={settings[`intro_text_${lang}`]}
                        onChange={(e) => handleChange(`intro_text_${lang}`, e.target.value)}
                      />
                    </div>

                    <div>
                      <Label>Tekst u podnožju</Label>
                      <Textarea
                        rows={2}
                        value={settings[`footer_text_${lang}`]}
                        onChange={(e) => handleChange(`footer_text_${lang}`, e.target.value)}
                      />
                    </div>

                    <div>
                      <Label>Tekst gumba</Label>
                      <Input
                        value={settings[`button_text_${lang}`]}
                        onChange={(e) => handleChange(`button_text_${lang}`, e.target.value)}
                      />
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Akcije</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Button onClick={saveSettings} disabled={saving} className="flex-shrink-0">
                  {saving ? 'Spremanje...' : 'Spremi postavke'}
                </Button>
              </div>
              
              <div className="border-t pt-4">
                <Label className="mb-2 block">Pošalji test email</Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="vas@email.com"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    className="flex-1"
                  />
                  <Button 
                    variant="outline" 
                    onClick={sendTestEmail} 
                    disabled={sendingTest || !apiKeyConfigured || !settings.sender_email || !testEmailAddress.trim()}
                    className="flex-shrink-0"
                  >
                    {sendingTest ? 'Slanje...' : 'Pošalji'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Unesite email adresu na koju želite primiti testni email</p>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default EmailSettingsManager;
